import { AdminApiError, type AdminClient, type ApprovalView } from '@apx/admin-sdk';
import { wireDatePickers } from '../date-picker.js';
import { askPrompt } from '../modal.js';
import { fmtDateTime } from '../format.js';
import { showToast } from '../toast.js';

export interface ApprovalsPageDeps {
  client: AdminClient;
  integratorId: string;
  /** Tenant staff: no cross-tenant view to link to, and no bulk page. */
  isLocked?: boolean;
}

export async function renderApprovalsPage(
  root: HTMLElement,
  deps: ApprovalsPageDeps,
): Promise<void> {
  root.innerHTML = `
    <h1 class="page-title">Pending approvals</h1>
    <p class="page-sub">Two-admin gate for tunable backend controls. A proposer creates a change; a second admin (with the same permission) approves or rejects.${
      deps.isLocked ? '' : ' This is one integrator’s queue — <a href="/approvals">see all integrators →</a>'
    }</p>
    <div class="card">
      <h2>Audit export</h2>
      <p class="muted" style="margin:-4px 0 8px;font-size:12px">Download the full approval audit trail (proposer, approver, reason, prev → pending diff, decision time) as CSV.</p>
      <div class="row" style="gap:12px;flex-wrap:wrap">
        <label>Status
          <select id="ax-status">
            <option value="all" selected>All</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </label>
        <span class="filter-field"><label for="ax-from">From</label><input id="ax-from" type="datetime-local" step="1" class="field-input" style="width:auto" /></span>
        <span class="filter-field"><label for="ax-to">To</label><input id="ax-to" type="datetime-local" step="1" class="field-input" style="width:auto" /></span>
        <button id="ax-export" class="primary">Export CSV</button>
        <span id="ax-msg" class="muted"></span>
      </div>
    </div>
    <div class="card">
      <h2>Pending</h2>
      <div id="approvals-pending"><p class="muted">Loading…</p></div>
    </div>
    <div class="card">
      <h2>Recently decided</h2>
      <div id="approvals-recent"><p class="muted">Loading…</p></div>
    </div>
  `;

  const exportBtn = root.querySelector<HTMLButtonElement>('#ax-export')!;
  const exportMsg = root.querySelector<HTMLElement>('#ax-msg')!;

  wireDatePickers(root);

  exportBtn.addEventListener('click', async () => {
    exportMsg.textContent = 'Generating…';
    exportMsg.className = 'muted';
    try {
      const statusEl = root.querySelector<HTMLSelectElement>('#ax-status')!;
      const fromEl = root.querySelector<HTMLInputElement>('#ax-from')!;
      const toEl = root.querySelector<HTMLInputElement>('#ax-to')!;
      const blob = await deps.client.exportApprovalsCsv(deps.integratorId, {
        status: statusEl.value as 'all' | 'pending' | 'approved' | 'rejected',
        from: fromEl.value ? new Date(fromEl.value).toISOString() : undefined,
        to: toEl.value ? new Date(toEl.value).toISOString() : undefined,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `approvals-${deps.integratorId.slice(0, 8)}-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      exportMsg.textContent = 'Downloaded.';
    } catch (err) {
      exportMsg.textContent = describe(err);
      exportMsg.className = 'error';
    }
  });

  await Promise.all([refreshPending(), refreshRecent()]);

  async function refreshPending(): Promise<void> {
    try {
      const pending = await deps.client.listApprovals(deps.integratorId, 'pending');
      const el = root.querySelector<HTMLElement>('#approvals-pending')!;
      el.innerHTML = renderTable(pending, true);
      bindButtons(el);
    } catch (err) {
      root.querySelector<HTMLElement>('#approvals-pending')!.innerHTML =
        `<p class="error">${escapeHtml(describe(err))}</p>`;
    }
  }

  async function refreshRecent(): Promise<void> {
    try {
      const [approved, rejected, reverted] = await Promise.all([
        deps.client.listApprovals(deps.integratorId, 'approved'),
        deps.client.listApprovals(deps.integratorId, 'rejected'),
        deps.client.listApprovals(deps.integratorId, 'reverted'),
      ]);
      const merged = [...approved, ...rejected, ...reverted]
        .sort((a, b) => (b.decidedAt ?? '').localeCompare(a.decidedAt ?? ''))
        .slice(0, 20);
      root.querySelector<HTMLElement>('#approvals-recent')!.innerHTML = renderTable(merged, false);
    } catch (err) {
      root.querySelector<HTMLElement>('#approvals-recent')!.innerHTML =
        `<p class="error">${escapeHtml(describe(err))}</p>`;
    }
  }

  function renderTable(rows: ApprovalView[], withActions: boolean): string {
    if (rows.length === 0) return '<p class="muted">None.</p>';
    return `<table>
      <thead><tr>
        <th>When</th><th>Game</th><th>Surface</th><th>Proposer</th>
        ${withActions ? '<th>Pending value</th><th></th>' : '<th>Status</th><th>Approver</th>'}
      </tr></thead>
      <tbody>${rows
        .map((a) => {
          const when = withActions
            ? fmtDateTime(a.createdAt)
            : fmtDateTime(a.decidedAt ?? a.createdAt);
          const proposer = `<code>${a.proposerUserId.slice(0, 8)}…</code>`;
          if (withActions) {
            // A bulk row is one slice of a platform-wide change, decided as a
            // whole — the server refuses a per-row approve/reject.
            const actions = a.batchId
              ? deps.isLocked
                ? '<span class="muted">Provider bulk change</span>'
                : `<a href="/bulk-config?batch=${a.batchId}">Bulk batch ${a.batchId.slice(0, 8)} →</a>`
              : `<button data-approve="${a.id}">Approve</button>
                <button data-reject="${a.id}" class="ghost">Reject</button>`;
            return `<tr>
              <td>${when}</td><td><code>${a.gameId}</code></td><td><code>${a.surface}</code></td><td>${proposer}</td>
              <td>${
                a.batchId && a.pendingConfig === null
                  ? '<span class="muted">reset to provider default</span>'
                  : `<div class="diff">${escapeHtml(JSON.stringify(a.pendingConfig))}</div>`
              }</td>
              <td>${actions}</td>
            </tr>`;
          }
          const approver = a.approverUserId ? `<code>${a.approverUserId.slice(0, 8)}…</code>` : '—';
          return `<tr>
            <td>${when}</td><td><code>${a.gameId}</code></td><td><code>${a.surface}</code></td><td>${proposer}</td>
            <td>${a.status}</td><td>${approver}</td>
          </tr>`;
        })
        .join('')}</tbody>
    </table>`;
  }

  function bindButtons(scope: HTMLElement): void {
    scope.querySelectorAll<HTMLButtonElement>('button[data-approve]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        try {
          await deps.client.approve(btn.dataset.approve!);
          await Promise.all([refreshPending(), refreshRecent()]);
        } catch (err) {
          showToast(describe(err), 'error');
        }
      });
    });
    scope.querySelectorAll<HTMLButtonElement>('button[data-reject]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const reason = await askPrompt('Rejection reason?');
        if (!reason) return;
        try {
          await deps.client.reject(btn.dataset.reject!, reason);
          await Promise.all([refreshPending(), refreshRecent()]);
        } catch (err) {
          showToast(describe(err), 'error');
        }
      });
    });
  }
}

function describe(err: unknown): string {
  if (err instanceof AdminApiError) return `${err.code}: ${err.message}`;
  return err instanceof Error ? err.message : String(err);
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      case "'":
        return '&#39;';
      default:
        return c;
    }
  });
}
